import React, { useState, useEffect, forwardRef } from "react"
import VariantReader from "aldoo-ra/VariantReader"
import LocalUserPreferences from "aldoo-ra/LocalUserPreferences"
import { twMerge } from "tailwind-merge"

const Checkbox = forwardRef(
  (
    {
      label,
      isOn = false,
      onChange,
      labelPosition = "right",
      labelClassName = "",
      name = "",
      className = "",
      render,
      variant = "default",
      styleOverrides = {},
      required = false,
      disabled = false,
      localUserPreferencesKey = null, // Key for storing user preferences
    },
    ref
  ) => {
    const variants = VariantReader.useVariant({
      componentName: "Checkbox",
      variant,
      styleOverrides,
    })

    const [checked, setChecked] = useState(() => {
      // Try to load from user preferences first, then fall back to isOn
      if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
        try {
          const savedState = LocalUserPreferences.get(localUserPreferencesKey)
          if (savedState !== undefined && typeof savedState === 'boolean') {
            return savedState
          }
        } catch (error) {
          console.warn("Failed to load checkbox preference:", error)
        }
      }
      return isOn
    })

    const handleToggle = () => {
      if (disabled) return
      const newValue = !checked
      setChecked(newValue)
      
      // Save to local user preferences if key is provided
      if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
        try {
          LocalUserPreferences.set(localUserPreferencesKey, newValue)
        } catch (error) {
          console.warn("Failed to save checkbox preference:", error)
        }
      }
      
      if (!onChange) return
      onChange(newValue)
    }

    useEffect(() => {
      setChecked(isOn)
    }, [isOn])

    // If render prop is provided, use it instead of default markup
    if (render) {
      return render({
        isOn: checked,
        toggle: handleToggle,
        disabled,
      })
    }

    // Default checkbox markup
    return (
      <div
        className={twMerge(
          twMerge(variants.container, disabled ? variants.disabled : ""),
          className
        )}
        onClick={handleToggle}
      >
        {/* If the label is positioned on the left */}
        {label && labelPosition === "left" && (
          <label
            className={twMerge(
              twMerge(variants.label, variants.label_left),
              labelClassName
            )}
          >
            {label} {required && <span className={variants.required}>*</span>}
          </label>
        )}

        {/* Checkbox itself */}
        <div
          role="checkbox"
          aria-checked={checked}
          aria-disabled={disabled}
          ref={ref}
          tabIndex={disabled ? -1 : 0}
          className={twMerge(
            variants.checkbox,
            checked ? variants.checkbox_checked : variants.checkbox_unchecked
          )}
        >
          {checked && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={variants.checkmark}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
          {!checked && <div className={variants.placeholder}></div>}
        </div>

        {/* If the label is positioned on the right */}
        {label && labelPosition === "right" && (
          <label
            className={twMerge(
              twMerge(variants.label, variants.label_right),
              labelClassName
            )}
          >
            {label} {required && <span className={variants.required}>*</span>}
          </label>
        )}
      </div>
    )
  }
)

Checkbox.displayName = "Checkbox"
export default Checkbox
